/*!
 * Copyright (c) Microsoft Corporation.
 * All rights reserved. See LICENSE in the project root for license information.
 */

import { CategoryHierarchy } from '@msdyn365-commerce/commerce-entities';
import {
    CacheType,
    createObservableDataAction,
    getCatalogId,
    IAction,
    IActionContext,
    IActionInput,
    IAny,
    ICreateActionContext,
    IGeneric,
    IRequestContext
} from '@msdyn365-commerce/core';
import { CategoriesDataActions } from '@msdyn365-commerce/retail-proxy';
import { generateCacheKey } from '@msdyn365-commerce-modules/retail-actions';

export class GetCategoriesFixedCacheInput implements IActionInput {
    public readonly maxItems: number;
    public readonly channelId: number;
    public readonly catalogId?: number;
    private readonly _requestContext: IRequestContext;
    private readonly _mappedToHierarchy: boolean;

    constructor(context: IRequestContext, mappedToHierarchy: boolean, maxItems?: number) {
        this._requestContext = context;
        this._mappedToHierarchy = mappedToHierarchy;
        this.maxItems = maxItems || 250;
        this.channelId = context?.apiSettings?.channelId ? Number(context.apiSettings.channelId) : 0;
        this.catalogId = getCatalogId(context);
    }

    public getCacheKey = (): string =>
        generateCacheKey(
            this._requestContext.apiSettings,
            { key: 'CustomerId', value: this._requestContext.user.isAuthenticated === false
                ? 'anonymous'
                : this._requestContext.user.isOBORequest
                    ? this._requestContext.cookies.getAccountSelectionCookie() || 'anonymous'
                    : this._requestContext.user.customerAccountNumber || 'anonymous'
             },
            { key: 'Top', value: this.maxItems },
            { key: 'CatalogId', value: this.catalogId }
        );
    public getCacheObjectType = (): string => (this._mappedToHierarchy ? 'CategoryHierarchyFixedCache' : 'CategoryFixedCache');
    public dataCacheType = (): CacheType => 'request';
}

export const createCategoriesInput = (inputData: ICreateActionContext<IGeneric<IAny>>): IActionInput => {
    const topItems = inputData.config?.topCategories && Number.parseInt(inputData.config.topCategories, 10);
    return new GetCategoriesFixedCacheInput(inputData.requestContext, false, topItems);
};

export async function getCategoriesAction(input: GetCategoriesFixedCacheInput, ctx: IActionContext): Promise<CategoryHierarchy[]> {
    return ((await CategoriesDataActions.getCategoriesAsync(
        {
            callerContext: ctx, bypassCache: 'getAndPut'
        },
        input.channelId,
        input.catalogId
    )) as unknown) as CategoryHierarchy[];
}

export const getCategoriesFixedCacheDataAction = createObservableDataAction({
    id: 'dlvry/get-categories',
    action: getCategoriesAction as IAction<CategoryHierarchy[]>,
    input: createCategoriesInput
});

export default getCategoriesFixedCacheDataAction;
